import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { fetchApi } from './fetchApi';


interface Kategorie {
  id: number;
  name: string;
}

interface Lieferant {
  id: number;
  firmenname: string;
}

interface ParentArtikelDaten {
  sku: string;
  bezeichnung: string;
  beschreibung: string;
  einkaufspreis: string;
  verkaufspreis: string;
  // ✅ bild_url entfernt (Parent hat kein eindeutig zuordenbares Bild)
  kategorie_id: string;
  lieferant_id: string;
  asin: string;
  amazon_sku: string;
  amazon_product_id: string;
  shopify_product_id: string;
}

export default function ParentBearbeiten() {
  const navigate = useNavigate();

  const { id } = useParams();
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);


  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [lieferanten, setLieferanten] = useState<Lieferant[]>([]);

  const [artikel, setArtikel] = useState<ParentArtikelDaten>({
    sku: '',
    bezeichnung: '',
    beschreibung: '',
    einkaufspreis: '',
    verkaufspreis: '',
    kategorie_id: '',
    lieferant_id: '',
    asin: '',
    amazon_sku: '',
    amazon_product_id: '',
    shopify_product_id: ''
  });

  // -------------------------------------------------------------
  // DATEN LADEN
  // -------------------------------------------------------------
  useEffect(() => {
    async function ladeDaten() {
      try {
        // Kategorien
        const resKat = await fetchApi('/api/kategorie');
        setKategorien(await resKat.json());

        // Lieferanten
        const resLief = await fetchApi('/api/lieferant');
        setLieferanten(await resLief.json());

        // Artikel
        const resArt = await fetchApi(`/api/artikel/${id}`);
        const a = await resArt.json();

        setArtikel({
          sku: a.sku || '',
          bezeichnung: a.bezeichnung || '',
          beschreibung: a.beschreibung || '',
          einkaufspreis: a.einkaufspreis ? String(a.einkaufspreis) : '',
          verkaufspreis: a.verkaufspreis ? String(a.verkaufspreis) : '',
          // ✅ bild_url absichtlich ignoriert
          kategorie_id: a.kategorie_id ? String(a.kategorie_id) : '',
          lieferant_id: a.lieferant_id ? String(a.lieferant_id) : '',
          asin: a.asin || '',
          amazon_sku: a.amazon_sku || '',
          amazon_product_id: a.amazon_product_id || '',
          shopify_product_id: a.shopify_product_id || ''
        });

        setLaden(false);
      } catch (err) {
        setFehler('Fehler beim Laden der Daten.');
        setLaden(false);
      }
    }

    ladeDaten();
  }, [id]);

  // -------------------------------------------------------------
  // HANDLERS
  // -------------------------------------------------------------
  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setArtikel(prev => ({ ...prev, [name]: value }));
  }

  function handleKategorieChange(e: SelectChangeEvent) {
    setArtikel(prev => ({ ...prev, kategorie_id: e.target.value }));
  }

  function handleLieferantChange(e: SelectChangeEvent) {
    setArtikel(prev => ({ ...prev, lieferant_id: e.target.value }));
  }

  // -------------------------------------------------------------
  // SPEICHERN
  // -------------------------------------------------------------
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setErfolg(null);

    const payload = {
      sku: artikel.sku,
      ean: null,
      bezeichnung: artikel.bezeichnung,
      beschreibung: artikel.beschreibung || null,

      // Parent-spezifisch IMMER NULL
      farbe: null,
      groesse: null,
      mindestbestand: null,
      fnsku: null,
      shopify_variant_id: null,

      einkaufspreis: artikel.einkaufspreis ? parseFloat(artikel.einkaufspreis) : null,
      verkaufspreis: artikel.verkaufspreis ? parseFloat(artikel.verkaufspreis) : null,

      // ✅ Parent hat kein eindeutig zuordenbares Bild → immer NULL
      bild_url: null,

      kategorie_id: artikel.kategorie_id ? parseInt(artikel.kategorie_id, 10) : null,

      lieferant_id: artikel.lieferant_id ? parseInt(artikel.lieferant_id, 10) : null,

      parent_id: null,

      asin: artikel.asin || null,
      amazon_sku: artikel.amazon_sku || null,
      amazon_product_id: artikel.amazon_product_id || null,
      shopify_product_id: artikel.shopify_product_id || null,

      barcode_intern: null
    };

    try {
      const res = await fetchApi(`/api/artikel/${id}`, {
  method: 'PUT',
  body: JSON.stringify(payload)
});


      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Fehler beim Speichern.');
      }

      setErfolg('Änderungen gespeichert.');
    } catch (err: any) {
  setFehler(
    err?.message === '401'
      ? 'Session abgelaufen. Bitte neu einloggen.'
      : err?.message || 'Fehler beim Speichern.'
  );
}

  }

  // -------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------
  if (laden) {
    return (
      <Container maxWidth="md">
        <Typography mt={4}>Lade Artikel…</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={3}>
        <Typography variant="h5">Parent-Artikel bearbeiten</Typography>
      </Box>

      {/* optional: Parent Info anzeigen, wenn du willst */}
      {/* <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{parentInfo}</Typography> */}

      {fehler && <Alert severity="error">{fehler}</Alert>}
      {erfolg && <Alert severity="success">{erfolg}</Alert>}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* SKU */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="SKU"
              name="sku"
              fullWidth
              required
              value={artikel.sku}
              onChange={handleChange}
            />
          </Grid>

          {/* Bezeichnung */}
          <Grid item xs={12}>
            <TextField
              label="Bezeichnung"
              name="bezeichnung"
              fullWidth
              required
              value={artikel.bezeichnung}
              onChange={handleChange}
            />
          </Grid>

          {/* Beschreibung */}
          <Grid item xs={12}>
            <TextField
              label="Beschreibung"
              name="beschreibung"
              fullWidth
              multiline
              rows={3}
              value={artikel.beschreibung}
              onChange={handleChange}
            />
          </Grid>

          {/* EK / VK */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Einkaufspreis"
              name="einkaufspreis"
              type="number"
              fullWidth
              value={artikel.einkaufspreis}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Verkaufspreis"
              name="verkaufspreis"
              type="number"
              fullWidth
              value={artikel.verkaufspreis}
              onChange={handleChange}
            />
          </Grid>

          {/* Kategorie */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Kategorie</InputLabel>
              <Select
                value={artikel.kategorie_id}
                label="Kategorie"
                onChange={handleKategorieChange}
              >
                {kategorien.map(k => (
                  <MenuItem key={k.id} value={k.id.toString()}>
                    {k.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Lieferant */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Lieferant</InputLabel>
              <Select
                value={artikel.lieferant_id}
                label="Lieferant"
                onChange={handleLieferantChange}
              >
                {lieferanten.map(l => (
                  <MenuItem key={l.id} value={l.id.toString()}>
                    {l.firmenname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Amazon & Shopify */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="ASIN"
              name="asin"
              fullWidth
              value={artikel.asin}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Amazon SKU"
              name="amazon_sku"
              fullWidth
              value={artikel.amazon_sku}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Amazon Product ID"
              name="amazon_product_id"
              fullWidth
              value={artikel.amazon_product_id}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Shopify Product ID"
              name="shopify_product_id"
              fullWidth
              value={artikel.shopify_product_id}
              onChange={handleChange}
            />
          </Grid>

          {/* Buttons */}
          <Grid item xs={12} display="flex" justifyContent="flex-start" gap={2}>
            <Button variant="contained" color="primary" type="submit">
              Speichern
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/artikel-liste')}
            >
              Zurück
            </Button>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
}
